import React, { ReactNode } from "react";
import { Box, Container, useMediaQuery, useTheme } from "@mui/material";
import { Helmet } from "react-helmet";
import { SideMenu } from "../../features/side_menu/SideMenu";
import { ScrollRestoration } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

export function PageContainer(props: {
  pageSeoTitle: string;
  pageDescription: string;
  children?: React.ReactNode;
}) {
  const { i18n } = useTranslation();
  const location = useLocation();

  // Define supported languages for hreflang
  const supportedLanguages = ["en", "es", "fr", "de", "pt", "it", "ru", "ja", "zh", "ar"];

  // Get current language from i18next (default to "en")
  const currentLang = supportedLanguages.includes(i18n.language) ? i18n.language : "en";

  const alternativeLanguages = supportedLanguages.filter(lang => lang !== currentLang);

  // Current pathname (e.g. "/en", "/en/bmi-calculator", "/")
  const path = location.pathname;

  // Strip the leading "/lang" or "/lang/" segment
  let relativeUrl = path.replace(/^\/[a-z]{2}(\/?)/, "");

  // If we are on the root ("/en" or "/"), relativeUrl should be empty string
  if (relativeUrl === "/" || relativeUrl === '') {
    relativeUrl = "";
  }
  else{
    relativeUrl = `/${relativeUrl}`;
  }

  // Canonical URL for the current page
  const canonicalUrl =`https://numberchomper.com/${currentLang}${relativeUrl}`;

  return (
    <Layout>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          m: 0,
          p: 0,
        }}
      >
        <Helmet>
          <title>{props.pageSeoTitle}</title>
          <meta name="description" content={props.pageDescription} />
          <link rel="canonical" href={canonicalUrl} />

          {/* Hreflang tags for international SEO */}
          {alternativeLanguages.map((lang) => {
            const langUrl = `https://numberchomper.com/${lang}${relativeUrl}`;
            return (
              <link key={lang} rel="alternate" hrefLang={lang} href={langUrl} />
            );
          })}

          {/* Default hreflang for unspecified languages */}
          <link rel="alternate" hrefLang="x-default" href={`https://numberchomper.com/en${relativeUrl}`} />

          {/* Open graph, social media sharing tags */}
          <meta property="og:type" content="website" />
          <meta property="og:title" content={props.pageSeoTitle} />
          <meta property="og:description" content={props.pageDescription} />
          <meta property="og:site_name" content="Number Chomper" />
          <meta property="og:image" content="https://numberchomper.com/favicon-512x512.png" />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={props.pageSeoTitle} />
          <meta name="twitter:description" content={props.pageDescription} />
          <meta name="twitter:image" content="https://numberchomper.com/favicon-512x512.png" />
        </Helmet>
        {props.children}
        <ScrollRestoration />
      </Box>
    </Layout>
  );
}

interface LayoutProps {
  children: ReactNode;
}

function Layout(props: LayoutProps) {
  const { children } = props;
  const theme = useTheme();
  const lessThanSm = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Container maxWidth="sm" disableGutters={lessThanSm}>
      {children}
    </Container>
  );
}
